<template>
  <div class="action-field">
    <!-- 動作類型 Tab -->
    <div class="action-type-tabs">
      <button
        v-for="t in visibleActionTypes"
        :key="t.value"
        type="button"
        class="type-tab"
        :class="{ active: currentType === t.value }"
        @click="setType(t.value)"
      >{{ t.label }}</button>
    </div>

    <!-- 網址 -->
    <div v-if="currentType === 'uri'" class="action-input">
      <input
        type="url"
        class="form-control"
        :class="{ 'input-error': uriError }"
        :value="val.uri || ''"
        @input="onUriInput($event.target.value)"
        @blur="validateUri(val.uri || '')"
        placeholder="https://..."
      />
      <span v-if="uriError" class="field-error"><i class="ri-error-warning-line"></i> {{ uriError }}</span>
    </div>

    <!-- 文字 -->
    <div v-else-if="currentType === 'message'" class="action-input">
      <input
        type="text"
        class="form-control"
        :value="val.text || ''"
        @input="patch('text', $event.target.value)"
        placeholder="點擊後發送的文字訊息"
      />
    </div>

    <!-- 圖文選單：邀請加入官方帳號（分享固定文案＋加入連結） -->
    <div v-else-if="currentType === 'share' && inviteOfficialAccount" class="action-input">
      <div v-if="inviteText" class="invite-preview">{{ inviteText }}</div>
      <span v-else class="field-error">
        <i class="ri-error-warning-line"></i> 請先確認這個 LINE 帳號已填寫 Basic ID（@開頭）
      </span>
      <span class="field-hint">
        點擊後開啟 LINE「分享給…」，好友會收到上面的文字與加入連結（電腦版 LINE 不支援）
      </span>
    </div>

    <!-- Flex：分享這張 Flex 訊息本身 -->
    <div v-else-if="currentType === 'share' && shareFlexMessage" class="action-input">
      <span class="field-hint">
        點擊後開啟 LINE「分享給…」，好友會收到這張 Flex 訊息（電腦版 LINE 不支援）
      </span>
    </div>

    <!-- 分享好友 -->
    <div v-else-if="currentType === 'share'" class="action-input">
      <input
        type="text"
        class="form-control"
        :value="val.shareText || ''"
        @input="patchAndComposeUri('shareText', $event.target.value)"
        placeholder="例：這是我的名片 https://..."
      />
      <span class="field-hint">
        點擊後開啟 LINE「分享給…」畫面，好友會收到這段文字（電腦版 LINE 不支援）
      </span>
    </div>

    <!-- 電話 -->
    <div v-else-if="currentType === 'tel'" class="action-input">
      <input
        type="tel"
        class="form-control"
        :value="val.phone || ''"
        @input="patchAndComposeUri('phone', $event.target.value)"
        placeholder="例：02-1234-5678"
      />
      <span class="field-hint">
        點擊後直接撥號，號碼中的「-」與空白會自動忽略
      </span>
    </div>

    <!-- Postback -->
    <template v-else-if="currentType === 'postback'">
      <div class="action-input">
        <div class="input-with-btn">
          <div v-if="matchedPostbackRule" class="postback-selected">
            <span class="postback-selected-label">已選規則</span>
            <span class="postback-selected-name" :title="val.data">{{ matchedPostbackRule.ruleName }}</span>
          </div>
          <input
            v-else
            type="text"
            class="form-control"
            :value="val.data || ''"
            @input="patch('data', $event.target.value)"
            placeholder="請點「選擇」從清單挑規則，或手動輸入 data"
          />
          <button type="button" class="btn-pick" @click="openPicker">
            <i class="ri-list-check"></i> 選擇
          </button>
        </div>
      </div>
      <div class="action-input" style="margin-top:6px;">
        <input
          type="text"
          class="form-control"
          :value="val.displayText || ''"
          @input="patch('displayText', $event.target.value)"
          @focus="($event) => $event.target.select()"
          placeholder="聊天室顯示的文字（不填則不顯示）"
        />
      </div>
    </template>

    <!-- Postback 選擇器 Modal -->
    <teleport to="body">
      <div v-if="pickerVisible" class="picker-overlay" @click.self="closePicker">
        <div class="picker-modal">
          <div class="picker-header">
            <span class="picker-title"><i class="ri-list-check"></i> 選擇 Postback 規則</span>
            <button type="button" class="icon-btn" @click="closePicker"><i class="ri-close-line"></i></button>
          </div>
          <div class="picker-search">
            <input v-model="pbSearch" type="text" class="form-control" placeholder="搜尋規則名稱..." />
          </div>
          <div class="picker-list">
            <div v-if="pbLoading" class="picker-empty">載入中...</div>
            <div v-else-if="filteredRules.length === 0" class="picker-empty">無符合的規則</div>
            <div
              v-for="rule in filteredRules"
              :key="rule.id"
              class="picker-item"
              @click="selectRule(rule)"
            >
              <div class="picker-item-name">{{ rule.ruleName }}</div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { postbackRuleService } from '@/api/modules/postbackRules.js';
import {
  buildActionUri,
  actionValueFromUri,
  buildOfficialAccountInviteText,
  buildOfficialAccountInviteUri
} from '@/utils/lineActionValue.js';

function normalizePbListResponse(res) {
  return res?.data?.data?.items || res?.data?.items || [];
}

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  /** 圖文選單等多 Tab 場景：在 Postback 右側顯示「切換 Tab」類型 */
  showSwitchTabOption: { type: Boolean, default: false },
  /** 圖文選單：把「分享好友」改成邀請加入目前官方帳號 */
  inviteOfficialAccount: { type: Boolean, default: false },
  /** Flex 訊息：分享的是這張 Flex，不是一段文字 */
  shareFlexMessage: { type: Boolean, default: false },
  officialAccountBasicId: { type: String, default: '' },
  officialAccountName: { type: String, default: '' }
});
const emit = defineEmits(['update:modelValue']);

const shareTypeLabel = computed(() => {
  if (props.inviteOfficialAccount) return '邀請加入官方帳號';
  if (props.shareFlexMessage) return '分享這個 Flex';
  return '分享好友';
});

const inviteText = computed(() => (
  props.inviteOfficialAccount
    ? buildOfficialAccountInviteText(props.officialAccountBasicId, props.officialAccountName)
    : ''
));

const visibleActionTypes = computed(() => {
  const base = [
    { value: 'uri', label: '網址' },
    { value: 'message', label: '文字' },
    { value: 'postback', label: 'Postback' },
    { value: 'share', label: shareTypeLabel.value },
    { value: 'tel', label: '電話' }
  ];
  if (props.showSwitchTabOption) {
    return [...base, { value: 'switchTab', label: '切換 Tab' }];
  }
  return base;
});

const EMPTY_VALUE = {
  actionType: 'uri', uri: '', text: '', data: '', displayText: '', phone: '', shareText: ''
};

const val = computed(() => {
  // 舊格式：純字串 URL（tel:、分享連結也一併反推回對應類型）
  if (!props.modelValue || typeof props.modelValue === 'string') {
    return { ...EMPTY_VALUE, ...actionValueFromUri(props.modelValue || '') };
  }
  return { ...EMPTY_VALUE, ...props.modelValue };
});

const currentType = computed(() => val.value.actionType || 'uri');

function setType(type) {
  if (type === 'switchTab') {
    emit('update:modelValue', {
      ...val.value,
      actionType: 'switchTab',
      uri: '',
      text: '',
      data: ''
    });
    return;
  }
  const next = { ...val.value, actionType: type };
  // 邀請加入官方帳號：分享文案固定為加入連結，不讓使用者改字
  if (type === 'share' && props.inviteOfficialAccount) {
    next.shareText = inviteText.value;
    next.uri = buildOfficialAccountInviteUri(props.officialAccountBasicId, props.officialAccountName);
  } else if (type === 'share' && props.shareFlexMessage) {
    // 分享的是整張 Flex，連結在存檔時才換成這張範本的 LIFF 網址
    next.shareText = '';
    next.uri = '';
  } else if (type === 'share' || type === 'tel') {
    // 分享好友／電話的 uri 是組出來的，切換類型時要重算
    next.uri = buildActionUri(next);
  } else if (type === 'uri' && (currentType.value === 'share' || currentType.value === 'tel')) {
    // 切回網址時不要把組出來的 tel:／分享連結留在網址欄
    next.uri = '';
  }
  emit('update:modelValue', next);
}

function patch(key, value) {
  emit('update:modelValue', { ...val.value, [key]: value });
}

/** 分享文字／電話號碼改動時，同步組出實際送給 LINE 的 uri */
function patchAndComposeUri(key, value) {
  const next = { ...val.value, [key]: value };
  emit('update:modelValue', { ...next, uri: buildActionUri(next) });
}

// URL 驗證
const uriError = ref('');

function validateUri(url) {
  if (!url) { uriError.value = ''; return true; }
  try {
    const u = new URL(url);
    if (!['http:', 'https:', 'line:'].includes(u.protocol)) {
      uriError.value = '僅支援 http://、https:// 或 line:// 開頭的網址';
      return false;
    }
    uriError.value = '';
    return true;
  } catch {
    uriError.value = '網址格式不正確，請確認包含 https://';
    return false;
  }
}

function onUriInput(value) {
  patch('uri', value);
  if (uriError.value) validateUri(value); // 已有錯誤時即時修正
}

// 切換類型時清除錯誤
watch(() => props.modelValue?.actionType, () => { uriError.value = ''; });

// 官方帳號變更時，已選「邀請加入」的按鈕要改成新的加入連結
watch(
  () => [props.inviteOfficialAccount, props.officialAccountBasicId, props.officialAccountName],
  () => {
    if (!props.inviteOfficialAccount || currentType.value !== 'share') return;
    const uri = buildOfficialAccountInviteUri(props.officialAccountBasicId, props.officialAccountName);
    if ((val.value.uri || '') === uri) return;
    emit('update:modelValue', {
      ...val.value,
      actionType: 'share',
      shareText: inviteText.value,
      uri
    });
  }
);

// Postback picker
const pickerVisible = ref(false);
const pbRules = ref([]);
const pbSearch = ref('');
const pbLoading = ref(false);

const filteredRules = computed(() => {
  const kw = pbSearch.value.trim().toLowerCase();
  if (!kw) return pbRules.value;
  return pbRules.value.filter(r =>
    r.ruleName?.toLowerCase().includes(kw)
  );
});

/** 目前 data 是否對應後台某條 Postback 規則（有則顯示規則名稱，不顯示 uuid） */
const matchedPostbackRule = computed(() => {
  const d = (val.value.data || '').trim();
  if (!d || !pbRules.value.length) return null;
  return pbRules.value.find(r => r.postbackData === d) || null;
});

async function fetchPbRules() {
  if (pbRules.value.length > 0) return;
  pbLoading.value = true;
  try {
    const res = await postbackRuleService.list({ limit: 200 });
    pbRules.value = normalizePbListResponse(res);
  } catch (e) {
    pbRules.value = [];
  } finally {
    pbLoading.value = false;
  }
}

watch(
  () => [val.value.actionType, val.value.data],
  () => {
    if (val.value.actionType !== 'postback') return;
    const d = (val.value.data || '').trim();
    if (d) fetchPbRules();
  },
  { immediate: true }
);

async function openPicker() {
  pickerVisible.value = true;
  pbSearch.value = '';
  await fetchPbRules();
}

function closePicker() {
  pickerVisible.value = false;
}

function selectRule(rule) {
  emit('update:modelValue', {
    ...val.value,
    data: rule.postbackData,
    // 沿用規則的「點選時顯示文字」。規則沒填就不顯示，不可改用規則名稱或按鈕名稱。
    displayText: String(rule.displayText ?? '').trim()
  });
  closePicker();
}
</script>

<style scoped>
.action-field { display: flex; flex-direction: column; gap: 6px; }

.action-type-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  row-gap: 6px;
}
.type-tab {
  padding: 4px 14px; border: 1px solid #d1d5db; border-radius: 20px;
  background: #fff; font-size: 12px; color: #6b7280; cursor: pointer; transition: all .15s;
  flex-shrink: 0;
}
.type-tab.active { border-color: #3b82f6; background: #eff6ff; color: #3b82f6; font-weight: 500; }
.type-tab:hover:not(.active) { border-color: #93c5fd; }

.action-input { display: flex; flex-direction: column; gap: 4px; }
.form-control {
  width: 100%; padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 13px; box-sizing: border-box;
}
.form-control:focus { outline: none; border-color: #3b82f6; }
.form-control.input-error { border-color: #ef4444; }
.form-control.input-error:focus { border-color: #ef4444; box-shadow: 0 0 0 2px rgba(239,68,68,.12); }
.field-error {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; color: #ef4444;
}
.field-error i { font-size: 12px; }
.field-hint { font-size: 11px; color: #9ca3af; line-height: 1.5; }
.invite-preview {
  white-space: pre-wrap;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}

.input-with-btn { display: flex; gap: 6px; align-items: stretch; }
.input-with-btn .form-control { flex: 1; min-width: 0; }

.postback-selected {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 7px 10px;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  background: #eff6ff;
}
.postback-selected-label {
  font-size: 10px;
  font-weight: 600;
  color: #3b82f6;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.postback-selected-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e3a8a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-pick {
  display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
  padding: 6px 12px; border: 1px solid #d1d5db; border-radius: 6px;
  background: #f9fafb; color: #374151; font-size: 12px; cursor: pointer; transition: all .15s;
}
.btn-pick:hover { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; }

.picker-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.4);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.picker-modal {
  background: #fff; border-radius: 12px; width: 480px; max-width: 90vw;
  max-height: 70vh; display: flex; flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,.18);
}
.picker-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid #f3f4f6;
}
.picker-title { font-size: 15px; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 6px; }
.picker-title i { color: #3b82f6; }
.icon-btn {
  width: 28px; height: 28px; border: 1px solid #d1d5db; border-radius: 6px;
  background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #6b7280; font-size: 14px;
}
.picker-search { padding: 12px 20px; border-bottom: 1px solid #f3f4f6; }
.picker-list { overflow-y: auto; flex: 1; padding: 8px 0; }
.picker-empty { text-align: center; padding: 32px; color: #9ca3af; font-size: 13px; }
.picker-item { padding: 10px 20px; cursor: pointer; transition: background .12s; }
.picker-item:hover { background: #f0f9ff; }
.picker-item-name { font-size: 13px; font-weight: 500; color: #374151; }
</style>
